import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { IconX } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { isGuestEmail, memberDisplayName } from '../../helpers/guest-helper'
import { isNeutralMatchLabel } from '../../helpers/restaurant-helpers'
import { ROUTES } from '../../helpers/routes-helper'
import useAppUtilities from '../../hooks/useAppUtilities'
import { useAppDispatch, useAppSelector } from '../../store'
import {
  fetchGroupRestaurantEnrichment,
  selectGroupRestaurantEnrichment,
  selectGroupRestaurantEnrichmentLoading,
} from '../../store/socialSlice'
import { Recommendation } from '../../types'
import { Member } from '../../types/groupTypes'
import TagChip from '../ui/TagChip'

interface GroupBreakdownDialogProps {
  open: boolean
  onClose: () => void
  restaurantAlias: string
  groupRecommendations?: Recommendation[]
  groupId?: string
  members?: Member[]
  groupName?: string
}

const GroupBreakdownDialog: React.FC<GroupBreakdownDialogProps> = ({
  open,
  onClose,
  restaurantAlias,
  groupRecommendations = [],
  groupId,
  members = [],
  groupName,
}) => {
  const { navigate, theme } = useAppUtilities()
  const dispatch = useAppDispatch()
  const enrichment = useAppSelector(
    selectGroupRestaurantEnrichment(restaurantAlias),
  )
  const enrichmentLoading = useAppSelector(
    selectGroupRestaurantEnrichmentLoading(restaurantAlias),
  )
  const [expandedSummaries, setExpandedSummaries] = useState<
    Record<number, boolean>
  >({})

  // Trigger enrichment when dialog opens
  useEffect(() => {
    if (!open) return
    if (!groupId || !restaurantAlias) return

    // If we already have enrichment for this alias, skip
    if (enrichment) return

    void dispatch(
      fetchGroupRestaurantEnrichment({ groupId, alias: restaurantAlias }),
    )
  }, [open, groupId, restaurantAlias, enrichment, dispatch])

  const findMemberByRecommendation = (
    rec: Recommendation,
  ): Member | undefined => {
    // Try to match by name (firstName from backend)
    if (rec.name) {
      const member = members.find((m) => {
        const memberFirstName = m.name?.split(' ')[0]?.toLowerCase()
        const recName = rec.name?.toLowerCase()
        return memberFirstName === recName || m.name?.toLowerCase() === recName
      })
      if (member) return member
    }

    // Try to match by email if available
    if (rec.email) {
      return members.find((m) => m.email === rec.email)
    }

    return undefined
  }

  const handleViewDishes = (member: Member): void => {
    if (!groupId) return

    // Pass the opaque eater id, never the email — keeps PII out of the URL (browser history,
    // Referer headers, and our own page_view analytics). Fall back to email only for legacy
    // members that somehow lack an eaterId, so the feature never silently breaks.
    const params = new URLSearchParams({ groupId })
    if (member.eaterId) {
      params.set('memberId', member.eaterId)
    } else if (member.email) {
      params.set('memberEmail', member.email)
    } else {
      return
    }

    navigate(`${ROUTES.RESTAURANT_DETAILS}/${restaurantAlias}/details?${params.toString()}`)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pt: 2,
          px: 2,
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '18px', sm: '20px' } }}>
          Group Member Breakdown
        </Typography>
        <IconX
          size={20}
          style={{ cursor: 'pointer' }}
          onClick={onClose}
          color={theme.palette.grey[600]}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          px: 2,
          py: 1,
        }}
      >
        {groupName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '13px', sm: '14px' } }}>
            Some members don&apos;t match well here. Click a name to see what
            they&apos;d eat.
          </Typography>
        )}
        {enrichmentLoading && !enrichment && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '12px', sm: '13px' } }}>
            Refining match details…
          </Typography>
        )}
        {enrichment?.matchSummary && (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 2,
              backgroundColor: theme.palette.grey[50],
              borderRadius: '8px',
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            {!isNeutralMatchLabel(enrichment.matchLabel) && (
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="primary"
                sx={{ mb: 0.5, fontSize: { xs: '13px', sm: '14px' } }}
              >
                {enrichment.matchLabel}
              </Typography>
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                opacity: 0.9,
                fontStyle: 'italic',
                lineHeight: 1.5,
                fontSize: { xs: '13px', sm: '14px' },
              }}
            >
              {enrichment.matchSummary}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupRecommendations.length > 0 ? (
            groupRecommendations.map((rec, idx) => {
              const member = findMemberByRecommendation(rec)
              const memberEmail = member?.email ?? rec.email
              // Guests have null name parts + a synthetic guest email — never render "Guest null"
              // or leak the deterministic guest-...@guest.resactly.local address.
              const displayName = memberDisplayName(
                member?.name ?? rec.name,
                memberEmail,
                `Member ${idx + 1}`,
              )
              const showEmail = !!member?.email && !isGuestEmail(member.email)
              const yesPercentage = Math.round(rec.yes ?? 0)
              const summary = rec.summary
              const isLongSummary = summary && summary.length > 250
              const isExpanded = expandedSummaries[idx] ?? false
              const displaySummary = isLongSummary && !isExpanded
                ? `${summary.substring(0, 250)}...`
                : summary

              return (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: '12px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      mb: summary ? 1 : 0,
                      gap: 1,
                      minHeight: '40px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                      <Avatar
                        src={member?.imageUrl}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `1px solid ${theme.palette.grey[300]}`,
                          flexShrink: 0,
                        }}
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: { xs: '14px', sm: '15px' },
                          }}
                        >
                          {displayName}
                        </Typography>
                        {showEmail && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: { xs: '12px', sm: '13px' },
                            }}
                          >
                            {member?.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {!isNeutralMatchLabel(rec.matchLabel) && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <TagChip
                          data={[
                            {
                              percentage: yesPercentage.toString(),
                              title: rec.matchLabel,
                            },
                          ]}
                          bgColor="orange"
                          hoverDisable={true}
                        />
                      </Box>
                    )}
                  </Box>
                  {summary ? (
                    <Box sx={{ mt: 1, mb: member && member.email ? 1 : 0 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          opacity: 0.9,
                          fontStyle: 'italic',
                          lineHeight: 1.5,
                          fontSize: { xs: '13px', sm: '14px' },
                        }}
                      >
                        {displaySummary}
                      </Typography>
                      {isLongSummary && (
                        <Button
                          size="small"
                          onClick={() => {
                            setExpandedSummaries((prev) => ({
                              ...prev,
                              [idx]: !prev[idx],
                            }))
                          }}
                          sx={{
                            textTransform: 'none',
                            mt: 0.5,
                            p: 0,
                            minWidth: 'auto',
                            fontSize: '0.75rem',
                            color: 'primary.main',
                          }}
                        >
                          {isExpanded ? 'Show less' : 'Show more...'}
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1, mb: member && member.email ? 1 : 0 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          opacity: 0.7,
                          fontStyle: 'italic',
                          fontSize: { xs: '13px', sm: '14px' },
                        }}
                      >
                        We don&apos;t have enough data about this member yet.
                      </Typography>
                    </Box>
                  )}
                  {member && member.email && (
                    <Box sx={{ mt: 1, pt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDishes(member)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontSize: { xs: '12px', sm: '13px' },
                        }}
                      >
                        View dishes
                      </Button>
                    </Box>
                  )}
                </Box>
              )
            })
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontSize: { xs: '13px', sm: '14px' } }}>
              No member recommendations available yet.
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default GroupBreakdownDialog
